/**
 * Mini Calendar - Date picker calendar widget.
 * Builds and displays a small calendar for date selection.
 */

// Declare external dependencies - most come from ../types/web/globals.d.ts
// frames must be declared locally as 'any' to allow custom frame properties
declare var frames: any;

// Module flag
const _MINICALENDAR_JS_ = true;

// Types
interface DatePickerField {
  validate: () => boolean;
  set: (value: string) => void;
  get: () => string;
  executeEvent: (event: string) => void;
  focus: () => void;
}

interface MyDateTimeInstance {
  setFromString: (str: string) => MyDateTimeInstance;
  getAsDate: () => Date;
}

interface MyDateInstance {
  setFromString: (str: string) => MyDateInstance;
  getAsDate: () => Date;
}

/**
 * Currently active date picker field
 */
let datePickerField: DatePickerField | null = null;

/**
 * Month names for display
 */
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Day initials for header
 */
const DAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Initialize the date picker (placeholder for future functionality)
 */
function initDatePicker(): void {
}

/**
 * Shows the calendar popup for a date field.
 *
 * @param field - The name of the date field
 */
function showCalendar(field: string): void {
  datePickerField = getElementObj(field);
  if (!datePickerField) return;
  if (!datePickerField.validate()) datePickerField.set("");
  let d: string | null = datePickerField.get();
  if (d <= "") d = null;
  frames.__datePickerFrame.document.body.innerHTML = buildCalendar(d);
  const p = findPosition(document.getElementById("_" + field + "PickerA")!);
  const br = getScrollBottomRightPosition();
  let x = p.left - 1;
  let y = p.top - 1;
  if (x + 194 > br.right) x -= 174;
  if (y + 120 > br.bottom) y -= 98;
  const datePickerDiv = document.getElementById("__datePickerDiv");
  if (datePickerDiv) {
    datePickerDiv.style.left = x + "px";
    datePickerDiv.style.top = y + "px";
    datePickerDiv.style.visibility = "visible";
  }
}

/**
 * Navigate to a different month in the calendar.
 *
 * @param y - The year to navigate to
 * @param m - The month to navigate to (1-12)
 */
function goNavMonth(y: number, m: number): void {
  frames.__datePickerFrame.document.body.innerHTML = buildCalendar(m + "/1/" + y);
}

/**
 * Handle day selection in the calendar.
 *
 * @param y - The selected year
 * @param m - The selected month (1-12)
 * @param d - The selected day
 */
function doDay(y: number, m: number, d: number): void {
  const datePickerDiv = document.getElementById("__datePickerDiv");
  if (datePickerDiv) {
    datePickerDiv.style.visibility = "hidden";
  }
  if (datePickerField != null) {
    datePickerField.set(m + "/" + d + "/" + y);
    datePickerField.executeEvent("change");
    datePickerField.focus();
    datePickerField = null;
  }
}

/**
 * Build the calendar HTML.
 *
 * @param dateParam - The date to display (optional, defaults to today)
 * @param styles - Whether to include CSS classes (default true)
 * @returns The HTML string for the calendar
 */
function buildCalendar(dateParam?: string | null, styles?: boolean): string {
  const doStyles = (styles == null || (styles != null && styles));
  const today = currentDateTime();
  let displayDate: Date;

  if (dateParam != null) {
    if (typeof MyDateTime === "function") {
      displayDate = (new MyDateTime()).setFromString(dateParam).getAsDate();
    } else if (typeof MyDate === "function") {
      displayDate = (new MyDate()).setFromString(dateParam).getAsDate();
    } else {
      displayDate = new Date(dateParam);
    }
  } else {
    displayDate = currentDateTime();
  }

  const firstDay = displayDate;
  firstDay.setDate(1);
  const y = displayDate.getFullYear();
  const m = displayDate.getMonth();
  const isLeapYear = ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0);
  const numDays = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let c = '<table width="190" cellpadding="0" cellspacing="0" border="0">';
  c += '<tr><td colspan="7"';
  if (doStyles) c += ' class="calMiniHeader"';
  c += '><table cellpadding="0" cellspacing="0" border="0" width="100%">';
  c += '<tr><td';
  if (doStyles) c += ' class="calMiniHeader"';

  let tDate = new Date(displayDate.toString());
  tDate.setFullYear(tDate.getFullYear() - 1);
  c += ' width="1%"><a href="javaScript:goNavMonth(' + tDate.getFullYear() + ', ' + (tDate.getMonth() + 1) + ')">';
  c += '<img src="' + IMG_ROOT + 'calendar/ico_prev_ext.gif" style="vertical-align:middle;border:0;margin:0" alt="Previous Year"></a></td>';

  c += '<td';
  if (doStyles) c += ' class="calMiniHeader"';
  tDate = new Date(displayDate.toString());
  tDate.setMonth((tDate.getMonth() - 1) % 12);
  if (tDate.getMonth() === 12) tDate.setFullYear(tDate.getFullYear() - 1);
  c += ' width="1%"><a href="javaScript:goNavMonth(' + tDate.getFullYear() + ', ' + (tDate.getMonth() + 1) + ')">';
  c += '<img src="' + IMG_ROOT + 'calendar/ico_prev.gif" style="vertical-align:middle;border:0;margin:0" alt="Previous Month"></a></td>';

  c += '<td';
  if (doStyles) c += ' class="calMiniHeader"';
  c += ' width="98%" align="center"><b>' + MONTH_NAMES[displayDate.getMonth()] + '&nbsp;' + y + '</b></td>';

  c += '<td';
  if (doStyles) c += ' class="calMiniHeader"';
  tDate = new Date(displayDate.toString());
  tDate.setMonth((tDate.getMonth() + 1) % 12);
  if (tDate.getMonth() < 1) tDate.setFullYear(tDate.getFullYear() + 1);
  c += ' width="1%" align="right"><a href="javaScript:goNavMonth(' + tDate.getFullYear() + ', ' + (tDate.getMonth() + 1) + ')">';
  c += '<img src="' + IMG_ROOT + 'calendar/ico_next.gif" style="vertical-align:middle;border:0;margin:0" alt="Next Month"></a></td>';

  c += '<td';
  if (doStyles) c += ' class="calMiniHeader"';
  tDate = new Date(displayDate.toString());
  tDate.setFullYear(tDate.getFullYear() + 1);
  c += ' width="1%" align="right"><a href="javaScript:goNavMonth(' + tDate.getFullYear() + ', ' + (tDate.getMonth() + 1) + ')">';
  c += '<img src="' + IMG_ROOT + 'calendar/ico_next_ext.gif" style="vertical-align:middle;border:0;margin:0" alt="Next Year"></a></td></tr>';
  c += '</table></td></tr><tr>';

  for (let i = 0; i < 7; i++) {
    c += '<td' + (doStyles ? ' class="calMiniDayOfWeek"' : '') + ' width="14%" align="center"><b>' + DAY_INITIALS[i] + '</b></td>';
  }
  c += '</tr>';

  let dayOfMonth = 1 - firstDay.getDay();
  let dayOfWeek = 0;
  while (dayOfMonth <= numDays[m] || dayOfWeek !== 0) {
    if (dayOfWeek % 7 === 0) c += '<tr>';
    if (dayOfMonth === today.getDate() && m === today.getMonth() && y === today.getFullYear()) {
      c += '<td' + (doStyles ? ' class="calMiniToday"' : '') + ' align="center">' + (doStyles ? '' : '<b>');
    } else {
      c += '<td' + (doStyles ? ' class="calMiniDay"' : '') + ' align="center">';
    }
    if (dayOfMonth > 0 && dayOfMonth <= numDays[m]) {
      c += '<a href="javaScript:doDay(' + y + ',' + (m + 1) + ',' + dayOfMonth + ')">' + dayOfMonth + '</a>';
    } else {
      c += '&nbsp;';
    }
    if (dayOfMonth === today.getDate() && m === today.getMonth() && y === today.getFullYear() && !doStyles) {
      c += '</b>';
    }
    c += '</td>';
    if (dayOfWeek % 7 === 6) c += '</tr>';
    dayOfWeek = (dayOfWeek + 1) % 7;
    dayOfMonth++;
  }
  c += '</table>';
  return c;
}

// Export for module usage
export {
  _MINICALENDAR_JS_,
  initDatePicker,
  showCalendar,
  goNavMonth,
  doDay,
  buildCalendar,
  MONTH_NAMES,
  DAY_INITIALS
};

// Export types
export type {
  DatePickerField,
  MyDateTimeInstance,
  MyDateInstance
};

// Expose on globalThis for backwards compatibility
(globalThis as any)._MINICALENDAR_JS_ = _MINICALENDAR_JS_;
(globalThis as any).initDatePicker = initDatePicker;
(globalThis as any).showCalendar = showCalendar;
(globalThis as any).goNavMonth = goNavMonth;
(globalThis as any).doDay = doDay;
(globalThis as any).buildCalendar = buildCalendar;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("minicalendar.js");
}
